import React from "react";
import { Link } from "react-router-dom";
import { Target, Eye, ChevronRight, CheckCircle2, Award, Users, BookOpen, Zap } from "lucide-react";

const CLASSROOM_IMG = "https://images.unsplash.com/photo-1580582932707-520aed937b7b?crop=entropy&cs=srgb&fm=jpg&q=85&w=700";

const usps = [
  {
    icon: <Award size={24} color="#F2C94C" />,
    title: "Strong HSC + Entrance Focus",
    desc: "Simultaneous preparation for board exams and competitive entrance examinations.",
  },
  {
    icon: <Users size={24} color="#F2C94C" />,
    title: "Experienced Faculty",
    desc: "Directors with deep subject expertise in Chemistry, Mathematics, and Biology.",
  },
  {
    icon: <BookOpen size={24} color="#F2C94C" />,
    title: "Regular Mock Tests",
    desc: "Frequent testing simulates real exam conditions and builds confidence.",
  },
  {
    icon: <Target size={24} color="#F2C94C" />,
    title: "Personalized Attention",
    desc: "Small batches ensure every student gets individual focus and guidance.",
  },
  {
    icon: <Zap size={24} color="#F2C94C" />,
    title: "Conceptual Learning",
    desc: "Emphasis on deep understanding of science concepts, not rote memorization.",
  },
  {
    icon: <CheckCircle2 size={24} color="#F2C94C" />,
    title: "Performance Tracking",
    desc: "Detailed assessment feedback to monitor and accelerate student progress.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-16 md:pt-20">
      {/* Page Header */}
      <section className="page-header py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: "#F2C94C" }}>
            Know Us Better
          </p>
          <h1
            className="text-4xl md:text-6xl font-extrabold text-white mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            About Trident Academy
          </h1>
          <p className="text-blue-200 text-base md:text-lg max-w-2xl mx-auto">
            Dedicated to nurturing tomorrow's doctors and engineers through excellence in science education.
          </p>
          <nav className="flex items-center justify-center gap-2 mt-6 text-xs text-blue-300">
            <Link to="/" className="hover:text-yellow-400 transition-colors">Home</Link>
            <ChevronRight size={12} />
            <span className="text-yellow-400">About Us</span>
          </nav>
        </div>
      </section>

      {/* OUR STORY */}
      <section data-testid="our-story-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#F2C94C" }}>
                Our Story
              </p>
              <h2
                className="text-3xl md:text-4xl font-extrabold mb-5"
                style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
              >
                Building Rankers Since 2020
              </h2>
              <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
                <p>
                  Trident Academy was founded in 2020 with a singular mission — to provide high-quality, 
                  affordable science coaching to students in Kandivali East and across Mumbai. 
                  Founded by experienced educators Dr. Sunil Choudhary, Er. Shrihari Poojary, and Dr. Sunny Singh, 
                  the academy was built on the belief that every student has the potential to excel.
                </p>
                <p>
                  Our founding directors brought together decades of teaching experience across Chemistry, 
                  Mathematics, and Biology to create a unique learning environment that goes beyond textbooks. 
                  We focus on conceptual clarity, regular assessments, and personalized mentorship to ensure 
                  every student reaches their full potential.
                </p>
                <p>
                  Since our inception, we've mentored over 1,000 students and celebrated more than 100 top 
                  rankers in NEET, JEE, MHT-CET, and HSC board examinations. Our student success stories 
                  speak to the effectiveness of our teaching methodology.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-6">
                {[
                  { value: "2020", label: "Year Founded" },
                  { value: "1000+", label: "Students" },
                  { value: "50+", label: "Top Rankers" },
                ].map((s, i) => (
                  <div key={i} className="text-center">
                    <div
                      className="text-3xl font-extrabold"
                      style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                    >
                      {s.value}
                    </div>
                    <div className="text-xs text-gray-400 font-semibold mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src={CLASSROOM_IMG}
                alt="Trident Academy Classroom"
                className="w-full rounded-2xl shadow-xl object-cover"
                style={{ height: "420px" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section data-testid="mission-vision-section" className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Mission */}
            <div
              className="rounded-2xl p-8 border-t-4"
              style={{ backgroundColor: "#0A2540", borderTopColor: "#F2C94C" }}
              data-testid="mission-card"
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "#F2C94C" }}
                >
                  <Target size={22} color="#0A2540" />
                </div>
                <h3
                  className="text-xl font-bold text-white"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Our Mission
                </h3>
              </div>
              <p className="text-blue-200 text-sm leading-relaxed">
                To provide exceptional science coaching that builds strong conceptual foundations, 
                nurtures academic excellence, and prepares students to succeed in HSC board examinations 
                and competitive entrance tests like NEET, JEE, and MHT-CET. We are committed to making 
                quality education accessible to every aspiring student in Mumbai.
              </p>
            </div>

            {/* Vision */}
            <div
              className="rounded-2xl p-8 bg-white border-t-4"
              style={{ borderTopColor: "#0A2540" }}
              data-testid="vision-card"
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "#0A2540" }}
                >
                  <Eye size={22} color="#F2C94C" />
                </div>
                <h3
                  className="text-xl font-bold"
                  style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                >
                  Our Vision
                </h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">
                To become Mumbai's most trusted science coaching institute, recognized for producing 
                top rankers who go on to become doctors, engineers, and scientists. We envision a future 
                where every student who walks through our doors leaves with the knowledge, confidence, and 
                skills to achieve their dreams and contribute positively to society.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* USPs */}
      <section data-testid="usps-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#F2C94C" }}>
              What Sets Us Apart
            </p>
            <h2
              className="text-3xl md:text-4xl font-extrabold"
              style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
            >
              Our Unique Selling Points
            </h2>
            <div className="section-divider mt-4"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {usps.map((usp, i) => (
              <div
                key={i}
                className="rounded-2xl p-6 border border-gray-100 card-hover bg-white flex gap-4"
                data-testid={`usp-card-${i}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "#0A2540" }}
                >
                  {usp.icon}
                </div>
                <div>
                  <h3
                    className="font-bold text-base mb-1.5"
                    style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                  >
                    {usp.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{usp.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTITUTE HIGHLIGHTS */}
      <section
        data-testid="highlights-section"
        className="py-14"
        style={{ background: "linear-gradient(135deg, #0A2540, #0d3362)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl md:text-3xl font-extrabold text-white text-center mb-10"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Our <span style={{ color: "#F2C94C" }}>Infrastructure & Facilities</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              "Modern Classrooms",
              "Digital Learning",
              "Study Material",
              "Library Resources",
              "Test Series",
              "Academic Counselling",
            ].map((item, i) => (
              <div
                key={i}
                className="text-center bg-white/10 rounded-xl py-5 px-3"
                data-testid={`facility-item-${i}`}
              >
                <CheckCircle2 size={24} color="#F2C94C" className="mx-auto mb-2" />
                <div className="text-white text-xs font-semibold">{item}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2
            className="text-2xl font-extrabold mb-3"
            style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
          >
            Ready to Join Trident Academy?
          </h2>
          <p className="text-gray-500 text-sm mb-6">
            Take the first step toward your dream career. Book a free counseling session today.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-btn"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-sm transition-all hover:opacity-90"
            style={{ backgroundColor: "#0A2540", color: "white" }}
          >
            Enquire Now <ChevronRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
